import React, { useEffect, useState } from 'react';
import { ArrowUpRight, ArrowRight, Star, ShieldCheck, MessageCircle } from 'lucide-react';
import { heroRotating, heroImages, whatsappLink } from '../mock';

export default function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % heroRotating.length), 2200);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative pt-[96px] sm:pt-[130px] lg:pt-[160px] pb-12 lg:pb-24 overflow-hidden bg-wash">
      <div className="section-pad max-w-[1440px] mx-auto">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-8 lg:gap-14 items-center">
          {/* Left */}
          <div className="animate-fade-up">
            <div className="inline-flex max-w-full items-center gap-2 sm:gap-3 pl-2 pr-3 sm:pr-4 py-1.5 rounded-full bg-white border border-black/5 shadow-sm mb-6 sm:mb-8">
              <span className="inline-flex items-center gap-1.5 text-[0.58rem] sm:text-[0.62rem] font-bold tracking-widest text-[#ff5a1f] bg-[#ff5a1f]/10 px-2 py-1 rounded-full shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5a1f] animate-pulse-dot" /> LIVE
              </span>
              <span key={idx} className="text-xs sm:text-sm text-[#4a463d] animate-fade-up truncate">{heroRotating[idx]}</span>
            </div>

            <h1 className="font-display text-[2.15rem] sm:text-[2.7rem] md:text-6xl lg:text-[4.4rem] leading-[1.02] font-extrabold text-[#16150f] tracking-tight">
              Best Car &amp; Bike
              <br /> PPF in <span className="relative inline-block text-accent-orange">Bangalore<span className="absolute left-0 -bottom-1 h-2 w-full rounded-full bg-[#ff5a1f]/20 -z-10" /></span>
            </h1>
            <p className="font-serif italic text-xl sm:text-2xl md:text-3xl text-[#6b6558] mt-3 sm:mt-4">Premium Paint Protection Film.</p>

            <p className="mt-5 sm:mt-7 text-[#5a5548] leading-relaxed max-w-lg text-[0.95rem] sm:text-[1rem]">
              <span className="text-[#16150f] font-semibold">VINYLED</span> provides premium car and bike PPF in Bangalore — protecting original paint from stone chips, scratches, road debris and everyday wear.
            </p>

            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <a href={whatsappLink()} target="_blank" rel="noreferrer" className="group inline-flex items-center justify-center gap-2 pl-6 pr-2 py-2 rounded-full text-sm font-semibold text-white bg-[#25D366] hover:bg-[#16150f] transition-all duration-300">
                Chat on WhatsApp
                <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-white" />
                </span>
              </a>
              <button onClick={() => scrollTo('#contact')} className="group inline-flex items-center justify-center gap-2 pl-6 pr-2 py-2 rounded-full text-sm font-semibold text-white bg-[#ff5a1f] hover:bg-[#16150f] transition-all duration-300">
                Start Your Project
                <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
                  <ArrowUpRight className="w-4 h-4 text-white" />
                </span>
              </button>
              <button onClick={() => scrollTo('#work')} className="group inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#16150f] py-2">
                View Our Work <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-[#ff5a1f] text-[#ff5a1f]" />)}</div>
                <span className="text-sm font-semibold text-[#16150f]">4.8</span>
                <span className="text-sm text-[#8a8578]">Google</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-black/10" />
              <div className="flex items-center gap-2 text-sm text-[#5a5548]"><ShieldCheck className="w-4 h-4 text-[#ff5a1f]" /> Up to 12Y Warranty</div>
            </div>
          </div>

          {/* Right image composition */}
          <div className="relative animate-fade-up" style={{ animationDelay: '0.12s' }}>
            <div className="lg:hidden relative h-[260px] sm:h-[340px] rounded-[1.4rem] overflow-hidden card-elevated">
              <img src={heroImages[0]} alt="PPF installation" className="w-full h-full object-cover" />
              <span className="absolute top-4 left-4 text-[0.6rem] font-semibold tracking-widest text-[#16150f] bg-white/90 backdrop-blur px-2.5 py-1 rounded-full">PPF INSTALLATION</span>
            </div>
            <div className="hidden lg:grid grid-cols-5 grid-rows-6 gap-3 h-[560px]">
              <div className="col-span-3 row-span-6 relative rounded-[1.6rem] overflow-hidden card-elevated group ring-1 ring-black/5">
                <img src={heroImages[0]} alt="PPF installation" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <span className="absolute top-4 left-4 text-[0.6rem] font-semibold tracking-widest text-[#16150f] bg-white/90 backdrop-blur px-2.5 py-1 rounded-full">PPF INSTALLATION</span>
              </div>
              <div className="col-span-2 row-span-3 relative rounded-[1.4rem] overflow-hidden card-elevated group">
                <img src={heroImages[3]} alt="BMW detailing" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="col-span-2 row-span-3 relative rounded-[1.4rem] overflow-hidden card-elevated group">
                <img src={heroImages[2]} alt="Surface protection" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            </div>

            {/* Floating stat card */}
            <div className="hidden lg:block absolute -left-8 bottom-8 glass-light rounded-2xl px-5 py-4 shadow-xl animate-float-slow">
              <p className="font-display text-2xl font-extrabold text-[#16150f]">6,346<span className="text-[#ff5a1f]">+</span></p>
              <p className="text-[0.68rem] tracking-widest text-[#8a8578] uppercase mt-0.5">Vehicles Protected</p>
            </div>
            <div className="hidden lg:block absolute -top-3 right-6 bg-white rounded-2xl px-4 py-3 shadow-xl border border-black/5 animate-float-slow" style={{ animationDelay: '1s' }}>
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-full bg-[#ff5a1f]/10 flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-[#ff5a1f]" /></span>
                <div>
                  <p className="text-xs font-bold text-[#16150f] leading-none">Certified</p>
                  <p className="text-[0.6rem] text-[#8a8578] mt-0.5">Installer Studio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrolling keyword strip */}
      <div className="mt-10 lg:mt-20 border-y border-black/8 py-3 sm:py-4 overflow-hidden">
        <div className="flex animate-strip w-max">
          {[...heroRotating, ...heroRotating, ...heroRotating].map((k, i) => (
            <span key={i} className="flex items-center gap-4 px-5 font-display text-sm sm:text-lg font-semibold text-[#c9c3b5] whitespace-nowrap">
              {k} <span className="text-[#ff5a1f]">/</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
